import React from 'react';
import { X, Mail, MessageSquare, FileText, Calendar, Database, Ticket, HelpCircle } from 'lucide-react';
import { Receipt } from '../types';

interface ReceiptDisplay extends Receipt {
  freshnessLabel: string;
}

function computeFreshness(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function toDisplayReceipts(receipts: Receipt[]): ReceiptDisplay[] {
  return receipts.map((r) => ({ ...r, freshnessLabel: computeFreshness(r.timestamp) }));
}

const sourceIcons: Record<Receipt['source_type'], React.FC<{ className?: string }>> = {
  email: Mail,
  slack: MessageSquare,
  doc: FileText,
  calendar: Calendar,
  crm: Database,
  ticket: Ticket,
  other: HelpCircle,
};

const sourceLabels: Record<Receipt['source_type'], string> = {
  email: 'Email',
  slack: 'Slack',
  doc: 'Document',
  calendar: 'Calendar',
  crm: 'CRM',
  ticket: 'Ticket',
  other: 'Source',
};

interface Props {
  receipts: Receipt[];
  decisionTitle: string;
  onClose: () => void;
}

const ReceiptsDrawer: React.FC<Props> = ({ receipts, decisionTitle, onClose }) => {
  const displayReceipts = toDisplayReceipts(receipts);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      />
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-[480px] bg-white z-50 shadow-2xl flex flex-col transform transition-transform duration-300 translate-x-0">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Evidence Receipts</h2>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{decisionTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close receipts drawer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Receipt list */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {displayReceipts.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No receipts for this decision.</p>
              <p className="text-xs mt-1">Confidence may be lower without source evidence.</p>
            </div>
          ) : (
            displayReceipts.map((receipt) => {
              const Icon = sourceIcons[receipt.source_type];
              return (
                <div
                  key={receipt.source_id}
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100"
                >
                  <div className="mt-0.5 p-1.5 bg-white rounded-md border border-gray-200 flex-shrink-0">
                    <Icon className="w-3.5 h-3.5 text-niwt-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{receipt.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {sourceLabels[receipt.source_type]} · Last seen: {receipt.freshnessLabel}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50">
          <p className="text-xs text-gray-400 text-center">
            Phase 2: receipts will link to live RAG citations
          </p>
        </div>
      </div>
    </>
  );
};

export default ReceiptsDrawer;
